/**
 * TruckCard Component
 * Individual truck row with editable fields and OCR capture
 */

import React from 'react';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  CircularProgress,
  Collapse,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  CameraAlt as CameraAltIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from '@mui/icons-material';
import { CustomDatePicker } from '../../../common/CustomDatePicker';
import { ServiceHistorySection } from './ServiceHistorySection';
import type { Truck, OCRFieldType, OCRLoadingState } from '../types';
import { VehicleAutocomplete } from './VehicleAutocomplete';

interface TruckCardProps {
  truck: Truck;
  expanded: boolean;
  ocrLoading: OCRLoadingState;
  serviceHistoryLoading: boolean;
  vinInputRef: (el: HTMLInputElement | null) => void;
  licenseInputRef: (el: HTMLInputElement | null) => void;
  onToggleExpand: (truckId: string) => void;
  onTruckUpdate: (truckId: string, field: string, value: string) => void;
  onRegistrationDateChange: (truckId: string, date: string) => void;
  onDeleteClick: (truck: Truck) => void;
  onVinCamera: (truckId: string) => void;
  onLicenseCamera: (truckId: string) => void;
  onImageCapture: (truckId: string, fieldType: OCRFieldType, event: React.ChangeEvent<HTMLInputElement>) => void;
  onAddServiceEntry: (truckId: string) => void;
  onUpdateServiceStatus: (truckId: string, entryId: string, currentStatus: string) => void;
  onUpdateServiceDate: (truckId: string, entryId: string, newDate: string) => void;
  onDeleteServiceEntry: (truckId: string, entryId: string) => void;
  canDelete?: boolean;
  canEditService?: boolean;
  onVehicleSelected?: (truckId: string, vehicle: any) => void;
}

export const TruckCard: React.FC<TruckCardProps> = ({
  truck,
  expanded,
  ocrLoading,
  serviceHistoryLoading,
  vinInputRef,
  licenseInputRef,
  onToggleExpand,
  onTruckUpdate,
  onRegistrationDateChange,
  onDeleteClick,
  onVinCamera,
  onLicenseCamera,
  onImageCapture,
  onAddServiceEntry,
  onUpdateServiceStatus,
  onUpdateServiceDate,
  onDeleteServiceEntry,
  canDelete = true,
  canEditService = true,
  onVehicleSelected,
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid #e5e7eb',
        borderRadius: 1.5,
        overflow: 'hidden',
      }}
    >
      {/* Main Vehicle Row */}
      <Box
        sx={{
          p: { xs: 1.5, sm: 2 },
          backgroundColor: '#ffffff',
          '&:hover': {
            backgroundColor: '#f9fafb',
          },
          transition: 'background-color 0.15s ease-in-out',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'flex-start' },
            gap: { xs: 1.5, sm: 2 },
          }}
        >
          {/* Desktop: Expand/Collapse Button (left side) */}
          <Box
            sx={{
              display: { xs: 'none', md: 'block' },
              width: '40px',
              flexShrink: 0,
            }}
          >
            <IconButton
              size="small"
              onClick={() => onToggleExpand(truck.id)}
              sx={{
                color: '#3b82f6',
              }}
            >
              {expanded ? (
                <KeyboardArrowUpIcon />
              ) : (
                <KeyboardArrowDownIcon />
              )}
            </IconButton>
          </Box>

          {/* Vehicle Information Section */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: 1.25, sm: 1.5 },
            }}
          >
            {/* Vehicle Name */}
            <Box>
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  mb: 0.5,
                  display: 'block',
                }}
              >
                Vehicle
              </Typography>
              <TextField
                value={truck.name}
                onChange={(e) => onTruckUpdate(truck.id, 'name', e.target.value)}
                variant="outlined"
                size="small"
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: '#000000',
                    backgroundColor: '#e5e7eb',
                    '& fieldset': {
                      borderColor: 'transparent',
                    },
                    '&:hover fieldset': {
                      borderColor: '#d1d5db',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#3b82f6',
                      borderWidth: 2,
                    },
                  },
                  '& input': {
                    padding: '6px 10px',
                  },
                }}
              />
            </Box>

            {/* VIN and License Plate Row */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1.25, sm: 1.5 },
              }}
            >
              {/* VIN with Camera */}
              <Box sx={{ flex: { xs: 1, sm: 2 } }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    mb: 0.5,
                    display: 'block',
                  }}
                >
                  VIN
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <VehicleAutocomplete
                    value={truck.vin}
                    field="vin"
                    onChange={(value) => onTruckUpdate(truck.id, 'vin', value)}
                    onVehicleSelected={(vehicle) => onVehicleSelected?.(truck.id, vehicle)}
                    placeholder="VIN Number"
                    disabled={ocrLoading[truck.id] === 'vin'}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: '#1f2937',
                        backgroundColor: '#e5e7eb',
                        '& fieldset': {
                          borderColor: 'transparent',
                        },
                        '&:hover fieldset': {
                          borderColor: '#d1d5db',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#3b82f6',
                          borderWidth: 2,
                        },
                      },
                      '& input': {
                        padding: '6px 10px',
                      },
                    }}
                  />
                  <input
                    ref={vinInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    style={{ display: 'none' }}
                    onChange={(e) => onImageCapture(truck.id, 'vin', e)}
                  />
                  <IconButton
                    onClick={() => onVinCamera(truck.id)}
                    disabled={ocrLoading[truck.id] === 'vin'}
                    sx={{
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      width: { xs: 44, sm: 32 },
                      height: { xs: 44, sm: 32 },
                      minWidth: { xs: 44, sm: 32 },
                      minHeight: { xs: 44, sm: 32 },
                      borderRadius: '4px',
                      '&:hover': {
                        backgroundColor: '#2563eb',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#9ca3af',
                        color: 'white',
                      },
                    }}
                  >
                    {ocrLoading[truck.id] === 'vin' ? (
                      <CircularProgress size={16} sx={{ color: 'white' }} />
                    ) : (
                      <CameraAltIcon sx={{ fontSize: { xs: 20, sm: 18 } }} />
                    )}
                  </IconButton>
                </Box>
              </Box>

              {/* License Plate with Camera */}
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    mb: 0.5,
                    display: 'block',
                  }}
                >
                  License Plate
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <VehicleAutocomplete
                    value={truck.licensePlate}
                    field="licensePlate"
                    onChange={(value) => onTruckUpdate(truck.id, 'licensePlate', value)}
                    onVehicleSelected={(vehicle) => onVehicleSelected?.(truck.id, vehicle)}
                    placeholder="License"
                    disabled={ocrLoading[truck.id] === 'license'}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: '#1f2937',
                        backgroundColor: '#e5e7eb',
                        '& fieldset': {
                          borderColor: 'transparent',
                        },
                        '&:hover fieldset': {
                          borderColor: '#d1d5db',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#3b82f6',
                          borderWidth: 2,
                        },
                      },
                      '& input': {
                        padding: '6px 10px',
                      },
                    }}
                  />
                  <input
                    ref={licenseInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    style={{ display: 'none' }}
                    onChange={(e) => onImageCapture(truck.id, 'licensePlate', e)}
                  />
                  <IconButton
                    onClick={() => onLicenseCamera(truck.id)}
                    disabled={ocrLoading[truck.id] === 'license'}
                    sx={{
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      width: { xs: 44, sm: 32 },
                      height: { xs: 44, sm: 32 },
                      minWidth: { xs: 44, sm: 32 },
                      minHeight: { xs: 44, sm: 32 },
                      borderRadius: '4px',
                      '&:hover': {
                        backgroundColor: '#2563eb',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#9ca3af',
                        color: 'white',
                      },
                    }}
                  >
                    {ocrLoading[truck.id] === 'license' ? (
                      <CircularProgress size={16} sx={{ color: 'white' }} />
                    ) : (
                      <CameraAltIcon sx={{ fontSize: { xs: 20, sm: 18 } }} />
                    )}
                  </IconButton>
                </Box>
              </Box>
            </Box>

            {/* Service Dates Section - Mobile Bottom Row */}
            <Box
              sx={{
                display: { xs: 'flex', md: 'none' },
                gap: 1.5,
              }}
            >
              {/* Registration Date */}
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    mb: 0.5,
                    display: 'block',
                  }}
                >
                  Registration
                </Typography>
                <CustomDatePicker
                  value={truck.registrationDate || ''}
                  onChange={(value) => onRegistrationDateChange?.(truck.id, value)}
                  placeholder="Oct 7"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: '#1f2937',
                      backgroundColor: '#e5e7eb',
                      '& fieldset': {
                        borderColor: 'transparent',
                      },
                      '&:hover fieldset': {
                        borderColor: '#d1d5db',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#3b82f6',
                        borderWidth: 2,
                      },
                    },
                    '& input': {
                      padding: '6px 10px',
                    },
                  }}
                />
              </Box>

              {/* Bi-Annual (Read-only) */}
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    mb: 0.5,
                    display: 'block',
                  }}
                >
                  Bi-Annual
                </Typography>
                <Box
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: '#1f2937',
                    backgroundColor: '#e5e7eb',
                    padding: '6px 10px',
                    borderRadius: '4px',
                    minHeight: '32px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {truck.biAnnualDate
                    ? new Date(truck.biAnnualDate).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                      })
                    : 'Not set'}
                </Box>
              </Box>
            </Box>

            {/* Mobile: Bottom Row with Expand Button and Delete */}
            <Box
              sx={{
                display: { xs: 'flex', md: 'none' },
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              {/* Expand/Collapse Button - Bottom Left */}
              <IconButton
                size="small"
                onClick={() => onToggleExpand(truck.id)}
                sx={{
                  color: '#3b82f6',
                  minWidth: 44,
                  minHeight: 44,
                }}
              >
                {expanded ? (
                  <KeyboardArrowUpIcon />
                ) : (
                  <KeyboardArrowDownIcon />
                )}
              </IconButton>

              {/* Delete Button - Bottom Right (Mobile) */}
              {canDelete && (
                <IconButton
                  size="small"
                  onClick={() => onDeleteClick(truck)}
                  sx={{
                    backgroundColor: '#ef4444',
                    color: 'white',
                    width: 44,
                    height: 44,
                    minWidth: 44,
                    minHeight: 44,
                    borderRadius: '4px',
                    '&:hover': {
                      backgroundColor: '#dc2626',
                    },
                  }}
                >
                  <DeleteIcon sx={{ fontSize: 20 }} />
                </IconButton>
              )}
            </Box>
          </Box>

          {/* Service Dates Section - Desktop Only */}
          <Box
            sx={{
              display: { xs: 'none', md: 'block' },
              width: '280px',
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: 1.5,
                mb: 1.5,
              }}
            >
              {/* Last Service Date */}
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    mb: 0.5,
                    display: 'block',
                  }}
                >
                  Registration
                </Typography>
                <CustomDatePicker
                  value={truck.registrationDate || ''}
                  onChange={(value) => onRegistrationDateChange?.(truck.id, value)}
                  placeholder="Oct 7"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: '#1f2937',
                      backgroundColor: '#e5e7eb',
                      '& fieldset': {
                        borderColor: 'transparent',
                      },
                      '&:hover fieldset': {
                        borderColor: '#d1d5db',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#3b82f6',
                        borderWidth: 2,
                      },
                    },
                    '& input': {
                      padding: '6px 10px',
                    },
                  }}
                />
              </Box>

              {/* Bi-Annual (Read-only calculated) */}
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    mb: 0.5,
                    display: 'block',
                  }}
                >
                  Bi-Annual
                </Typography>
                <Box
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: '#1f2937',
                    backgroundColor: '#e5e7eb',
                    padding: '6px 10px',
                    borderRadius: '4px',
                    minHeight: '32px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {truck.biAnnualDate
                    ? new Date(truck.biAnnualDate).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                      })
                    : 'Not set'}
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Delete Button - Desktop Only */}
          {canDelete && (
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                width: '40px',
                flexShrink: 0,
                alignItems: 'flex-start',
                justifyContent: 'center',
                mt: 2.875,
                ml: 1.5,
              }}
            >
              <IconButton
                size="small"
                onClick={() => onDeleteClick(truck)}
                sx={{
                  backgroundColor: '#ef4444',
                  color: 'white',
                  width: 32,
                  height: 32,
                  minWidth: 32,
                  minHeight: 32,
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: '#dc2626',
                  },
                }}
              >
                <DeleteIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          )}
        </Box>
      </Box>

      {/* Collapsible Service History */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <ServiceHistorySection
          truck={truck}
          loading={serviceHistoryLoading}
          onAddServiceEntry={onAddServiceEntry}
          onUpdateServiceStatus={onUpdateServiceStatus}
          onUpdateServiceDate={onUpdateServiceDate}
          onDeleteServiceEntry={onDeleteServiceEntry}
          canEdit={canEditService}
        />
      </Collapse>
    </Paper>
  );
};
